import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight, MessageCircle, CheckCircle2, X, Check, XCircle, Plus } from "lucide-react";
import PhotoUpload from "../components/PhotoUpload";
import WeddingStyleStep, { isWeddingStyleValid } from "../components/WeddingStyleStep";
import {
    QuestionnaireShell,
    Field,
    TextInput,
    OptionTile,
    PrimaryButton,
} from "../components/QuestionnaireShell";
import { otpSend, otpVerify, submitCoupleQuestionnaire } from "../lib/questionnaire";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import { ConsentBlock } from "../components/ConsentBlock";
import { LegalFooter } from "../components/LegalFooter";

const EVENT_OPTIONS = [
    { value: "wedding", label: "Wedding" },
    { value: "reception", label: "Reception" },
    { value: "engagement", label: "Engagement" },
    { value: "haldi", label: "Haldi" },
    { value: "mehendi", label: "Mehendi" },
    { value: "sangeet", label: "Sangeet" },
    { value: "cocktail", label: "Cocktail" },
    { value: "baraat", label: "Baraat" },
    { value: "outdoor photoshoot", label: "Outdoor photoshoot" },
    { value: "other", label: "Other" },
];

const VENUE_OPTIONS = [
    { value: "palace", label: "Palace / Heritage" },
    { value: "hotel", label: "5-Star Hotel" },
    { value: "beach", label: "Beach" },
    { value: "outdoor garden", label: "Outdoor Garden" },
    { value: "farmhouse", label: "Farmhouse" },
    { value: "banquet", label: "Banquet Hall" },
    { value: "temple", label: "Temple" },
    { value: "home", label: "Home" },
];

const STYLE_OPTIONS = [
    { value: "traditional", label: "Traditional" },
    { value: "modern", label: "Modern" },
    { value: "fusion", label: "Fusion" },
    { value: "minimal", label: "Minimal" },
    { value: "regal", label: "Regal" },
    { value: "boho", label: "Boho" },
    { value: "editorial", label: "Editorial" },
];

const DO_TIPS = [
    "Clear face",
    "Natural lighting",
    "Different angles",
    "Different expressions",
    "At least one upper-body / full-body photo",
];
const DONT_TIPS = [
    "Sunglasses",
    "Heavy filters",
    "Very dark photos",
    "Group photos",
    "Extreme angles",
    "Very old photos",
    "Face partially covered",
];

// Per-event card used in Step 3 — one card = one ceremony with its own
// name, venue and style so the pipeline can render distinct visuals for each.
function EventCard({ index, ev, onChange, onRemove }) {
    const isMain = index === 0;
    const ordinal = ["Main event", "Second event", "Third event", "Fourth event", "Fifth event"][index] || `Event ${index + 1}`;
    return (
        <div
            className="rounded-[20px] bg-white p-5 shadow-sm ring-1 ring-charcoal/10 md:p-6"
            data-testid={`event-card-${index}`}
        >
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span
                        className={`inline-flex h-6 items-center rounded-full px-2.5 text-[10px] uppercase tracking-[0.18em] ${
                            isMain ? "bg-charcoal text-ivory" : "bg-champagne text-charcoal"
                        }`}
                    >
                        {isMain ? "Main" : `#${index + 1}`}
                    </span>
                    <span className="text-[13px] font-medium text-charcoal">{ordinal}</span>
                </div>
                {onRemove && (
                    <button
                        type="button"
                        onClick={onRemove}
                        data-testid={`btn-remove-event-${index}`}
                        aria-label={`Remove ${ordinal}`}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-rose-50 text-rose-500 transition hover:bg-rose-100"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>

            <div className="space-y-4">
                <div>
                    <div className="text-[10.5px] uppercase tracking-[0.22em] text-charcoal/55">Ceremony</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {EVENT_OPTIONS.map((o) => (
                            <button
                                key={o.value}
                                type="button"
                                onClick={() => onChange({ name: o.value })}
                                data-testid={`event-${index}-name-${o.value.replace(/\s+/g, "-")}`}
                                className={`rounded-full px-3 py-1.5 text-[12px] transition ring-1 ${
                                    ev.name === o.value
                                        ? "bg-charcoal text-ivory ring-charcoal"
                                        : "bg-white text-charcoal/70 ring-charcoal/10 hover:ring-charcoal/30"
                                }`}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="text-[10.5px] uppercase tracking-[0.22em] text-charcoal/55">Venue</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {VENUE_OPTIONS.map((o) => (
                            <button
                                key={o.value}
                                type="button"
                                onClick={() => onChange({ venue_type: o.value })}
                                data-testid={`event-${index}-venue-${o.value.replace(/\s+/g, "-")}`}
                                className={`rounded-full px-3 py-1.5 text-[12px] transition ring-1 ${
                                    ev.venue_type === o.value
                                        ? "bg-charcoal text-ivory ring-charcoal"
                                        : "bg-white text-charcoal/70 ring-charcoal/10 hover:ring-charcoal/30"
                                }`}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="text-[10.5px] uppercase tracking-[0.22em] text-charcoal/55">Style</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {STYLE_OPTIONS.map((o) => (
                            <button
                                key={o.value}
                                type="button"
                                onClick={() => onChange({ style: o.value })}
                                data-testid={`event-${index}-style-${o.value}`}
                                className={`rounded-full px-3 py-1.5 text-[12px] transition ring-1 ${
                                    ev.style === o.value
                                        ? "bg-charcoal text-ivory ring-charcoal"
                                        : "bg-white text-charcoal/70 ring-charcoal/10 hover:ring-charcoal/30"
                                }`}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// Simple chip input — user types text, Enter/comma adds a chip; click × removes.
// Used for favourite / avoid colours in the colour brief step.
function ChipInput({ values, setValues, placeholder, testId }) {
    const [draft, setDraft] = useState("");
    function commit() {
        const v = draft.trim();
        if (!v) return;
        if (values.some((x) => x.toLowerCase() === v.toLowerCase())) {
            setDraft("");
            return;
        }
        setValues((prev) => [...prev, v]);
        setDraft("");
    }
    function onKeyDown(e) {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit();
        } else if (e.key === "Backspace" && !draft && values.length) {
            setValues((prev) => prev.slice(0, -1));
        }
    }
    return (
        <div className="rounded-[14px] bg-white p-3 ring-1 ring-charcoal/10" data-testid={testId}>
            <div className="flex flex-wrap items-center gap-2">
                {values.map((v, i) => (
                    <span
                        key={`${v}-${i}`}
                        data-testid={`${testId}-chip-${i}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-champagne/60 px-2.5 py-1 text-[12px] text-charcoal"
                    >
                        {v}
                        <button
                            type="button"
                            onClick={() => setValues((prev) => prev.filter((_, idx) => idx !== i))}
                            data-testid={`${testId}-remove-${i}`}
                            className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-charcoal/10 text-charcoal/70 hover:bg-charcoal/25"
                            aria-label={`Remove ${v}`}
                        >
                            <X className="h-2.5 w-2.5" />
                        </button>
                    </span>
                ))}
                <input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKeyDown}
                    onBlur={commit}
                    placeholder={values.length ? "" : placeholder}
                    data-testid={`${testId}-input`}
                    className="min-w-[140px] flex-1 bg-transparent px-1 py-1 text-[13px] text-charcoal outline-none placeholder:text-charcoal/40"
                />
            </div>
            {values.length === 0 && (
                <p className="mt-1 text-[10.5px] text-charcoal/50">Tip: press Enter after each colour.</p>
            )}
        </div>
    );
}

function PhotoGuidelines() {
    return (
        <div
            className="rounded-[16px] bg-champagne/30 p-4 ring-1 ring-charcoal/5"
            data-testid="photo-guidelines"
        >
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-charcoal/60">
                Photo guidelines · for the best AI match
            </div>
            <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div>
                    <div className="mb-1.5 text-[11.5px] font-medium text-emerald-700">✓ Do</div>
                    <ul className="space-y-1 text-[12px] text-charcoal/80">
                        {DO_TIPS.map((t) => (
                            <li key={t} className="flex items-start gap-1.5">
                                <Check className="mt-0.5 h-3 w-3 flex-none text-emerald-600" />
                                {t}
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <div className="mb-1.5 text-[11.5px] font-medium text-rose-700">✗ Avoid</div>
                    <ul className="space-y-1 text-[12px] text-charcoal/80">
                        {DONT_TIPS.map((t) => (
                            <li key={t} className="flex items-start gap-1.5">
                                <XCircle className="mt-0.5 h-3 w-3 flex-none text-rose-500" />
                                {t}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

// Compact "+ 4 more reference photos" gallery. Reuses PhotoUpload internally
// for each slot so validation (MediaPipe + phash dedup) is identical to the
// primary. No slot is required — the user can skip and submit with 0 extras.
function ExtraPhotos({ person, primaryPhash, extras, setExtras }) {
    const MAX = 4;
    const label = person === "bride" ? "Bride's" : "Groom's";
    function addExtra(payload) {
        setExtras((prev) => prev.length >= MAX ? prev : [...prev, payload]);
    }
    function removeAt(i) {
        setExtras((prev) => prev.filter((_, idx) => idx !== i));
    }
    const slotsRemaining = MAX - extras.length;

    return (
        <div className="space-y-3" data-testid={`extra-photos-${person}`}>
            <div className="flex items-baseline justify-between gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-gold">
                        {label} extra reference photos
                    </div>
                    <div className="text-[12px] text-charcoal/60">
                        Recommended: 4 more. Different angles + expressions dramatically improve AI face-match.
                    </div>
                </div>
                <span
                    className="rounded-full bg-charcoal/5 px-2.5 py-1 text-[10.5px] tracking-[0.14em] text-charcoal/70"
                    data-testid={`extra-photos-${person}-count`}
                >
                    {extras.length} / {MAX}
                </span>
            </div>

            {extras.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                    {extras.map((x, i) => (
                        <div
                            key={i}
                            className="group relative aspect-square overflow-hidden rounded-[12px] bg-champagne/40 ring-1 ring-charcoal/5"
                        >
                            <img
                                src={`data:image/jpeg;base64,${x.image_base64}`}
                                alt=""
                                className="h-full w-full object-cover"
                            />
                            <button
                                type="button"
                                onClick={() => removeAt(i)}
                                data-testid={`extra-photos-${person}-remove-${i}`}
                                className="absolute right-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-charcoal/80 text-ivory opacity-0 transition group-hover:opacity-100"
                                aria-label="Remove photo"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {slotsRemaining > 0 && (
                <PhotoUpload
                    key={extras.length /* remount so the widget resets after each add */}
                    label={`Add another photo (${slotsRemaining} left)`}
                    subLabel={extras.length === 0
                        ? "Try a side profile, a full-body shot, and a candid smile."
                        : "Different angle / expression / lighting than the ones above."}
                    onValid={(payload) => addExtra(payload)}
                    onClear={() => {}}
                    compareAgainstPhash={primaryPhash}
                    testIdPrefix={`extra-${person}-${extras.length}`}
                />
            )}
        </div>
    );
}

export default function CoupleQuestionnaire() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const packParam = parseInt(searchParams.get("pack"), 10);
    const preselectedPack = [1, 2, 3, 4, 5].includes(packParam) ? packParam : null;
    const [step, setStep] = useState(1);

    const [brideName, setBrideName] = useState("");
    const [bride, setBride] = useState(null); // {image_base64, phash, face_crop_base64}
    const [brideExtras, setBrideExtras] = useState([]); // Array<{image_base64}>

    const [groomName, setGroomName] = useState("");
    const [groom, setGroom] = useState(null);
    const [groomExtras, setGroomExtras] = useState([]);

    // Phase C.3 — multi-event brief: 1 main + up to 4 secondary events.
    // Each entry has {name, venue_type, style}. eventsBrief[0] is the main.
    const [eventsBrief, setEventsBrief] = useState([
        { name: "", venue_type: "", style: "" },
    ]);

    const [phone, setPhone] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [devOtp, setDevOtp] = useState("");
    const [otp, setOtp] = useState("");
    const [otpVerified, setOtpVerified] = useState(false);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    // Phase C — colour brief fields (events chip row retired — events are now
    // captured per-event in Step 3).
    const [culturalPref, setCulturalPref] = useState("");
    const [favColors, setFavColors] = useState([]);
    const [avoidColors, setAvoidColors] = useState([]);
    const [venueCity, setVenueCity] = useState("");
    // Feb-2026 patch — Wedding Style selection. Threads into Claude + Nano
    // Banana as the cultural styling directive. Required to advance past step 4.
    const [weddingStyle, setWeddingStyle] = useState(null);

    // Consent checkboxes — both required to submit for a free report. Also
    // required (fresh consent) at the paid unlock gate. Wording matches the
    // legal copy on the report cover.
    const [ownershipConsent, setOwnershipConsent] = useState(false);
    const [aiDisclaimerConsent, setAiDisclaimerConsent] = useState(false);

    const canNext = useMemo(() => {
        if (step === 1) return Boolean(brideName.trim() && bride);
        if (step === 2) return Boolean(groomName.trim() && groom);
        if (step === 3) {
            // At least the main event must be fully filled. Any partially-
            // filled secondary event blocks continue so users don't accidentally
            // submit half-broken briefs.
            return eventsBrief.every(
                (ev) => ev.name && ev.venue_type && ev.style,
            );
        }
        if (step === 4) return isWeddingStyleValid(weddingStyle);
        if (step === 5) return Boolean(phone && otpVerified && ownershipConsent && aiDisclaimerConsent);
        return false;
    }, [step, brideName, bride, groomName, groom, eventsBrief, weddingStyle, phone, otpVerified, ownershipConsent, aiDisclaimerConsent]);

    async function sendOtp() {
        if (!phone || phone.replace(/\D/g, "").length < 10) {
            toast.error("Enter a valid 10-digit WhatsApp number.");
            return;
        }
        setLoading(true);
        try {
            const res = await otpSend(phone);
            setOtpSent(true);
            setDevOtp(res.dev_otp || "");
            toast.success(`OTP sent (mocked). Use ${res.dev_otp} to verify.`);
        } catch (err) {
            toast.error(extractError(err, "Could not send OTP."));
        } finally {
            setLoading(false);
        }
    }

    async function verifyOtp() {
        if (!otp) return;
        setLoading(true);
        try {
            await otpVerify(phone, otp);
            setOtpVerified(true);
            toast.success("Number verified.");
        } catch (err) {
            toast.error(extractError(err, "Incorrect OTP."));
        } finally {
            setLoading(false);
        }
    }

    async function submit() {
        setLoading(true);
        try {
            const cleanEvents = eventsBrief
                .filter((ev) => ev.name && ev.venue_type && ev.style)
                .slice(0, 5)
                .map((ev, i) => ({
                    name: ev.name,
                    venue_type: ev.venue_type,
                    style: ev.style,
                    is_main: i === 0,
                }));
            const res = await submitCoupleQuestionnaire({
                bride_name: brideName,
                groom_name: groomName,
                bride_image_base64: bride.image_base64,
                groom_image_base64: groom.image_base64,
                bride_extra_images_base64: brideExtras.map((x) => x.image_base64),
                groom_extra_images_base64: groomExtras.map((x) => x.image_base64),
                events_brief: cleanEvents,
                // Legacy mirrors so old backends stay green during rollout.
                event: cleanEvents[0]?.name,
                venue: cleanEvents[0]?.venue_type,
                style: cleanEvents[0]?.style,
                phone,
                otp_verified: true,
                cultural_preference: culturalPref,
                favorite_colors: favColors,
                avoid_colors: avoidColors,
                venue_city: venueCity,
                wedding_style: weddingStyle,
                email: email || null,
                ownership_consent: ownershipConsent,
                ai_disclaimer_consent: aiDisclaimerConsent,
            });
            toast.success("Your details are saved — let's get your pack ready.");
            navigate(`/result/${res.id}${preselectedPack ? `?pack=${preselectedPack}` : ""}`);
        } catch (err) {
            toast.error(extractError(err, "Submission failed."));
        } finally {
            setLoading(false);
        }
    }

    function goNext() {
        if (step === 5) {
            submit();
        } else {
            setStep((s) => s + 1);
        }
    }

    const titles = {
        1: "Upload the bride's photo.",
        2: "Now the groom's photo.",
        3: "A little about the wedding.",
        4: "Your colour brief.",
        5: "Verify your WhatsApp number.",
    };

    return (
        <QuestionnaireShell
            title={titles[step]}
            step={step}
            total={5}
            onBack={step > 1 ? () => setStep(step - 1) : null}
        >
            {preselectedPack && (
                <div
                    className="mb-8 flex items-center gap-3 rounded-[16px] bg-champagne/40 p-4 text-[13px] text-charcoal ring-1 ring-charcoal/5"
                    data-testid="preselected-pack-banner"
                >
                    <CheckCircle2 className="h-4 w-4 flex-none text-gold" />
                    <span>
                        Booking the <strong>{preselectedPack}-event</strong> pack. Brief {preselectedPack === 1 ? "your ceremony" : `all ${preselectedPack} events`} below to make full use of it.
                    </span>
                </div>
            )}
            {step === 1 && (
                <div className="space-y-6" data-testid="step-bride">
                    <Field label="Bride's name" required testId="field-bride-name">
                        <TextInput
                            placeholder="e.g. Aanya"
                            value={brideName}
                            onChange={(e) => setBrideName(e.target.value)}
                            data-testid="input-bride-name"
                        />
                    </Field>
                    <PhotoGuidelines />
                    <PhotoUpload
                        label="Primary bride photo"
                        subLabel="Used for skin-tone analysis + as her main reference. Clear, well-lit, front-facing."
                        onValid={setBride}
                        onClear={() => { setBride(null); setBrideExtras([]); }}
                        testIdPrefix="bride"
                    />
                    {bride && (
                        <ExtraPhotos
                            person="bride"
                            primaryPhash={bride.phash}
                            extras={brideExtras}
                            setExtras={setBrideExtras}
                        />
                    )}
                </div>
            )}

            {step === 2 && (
                <div className="space-y-6" data-testid="step-groom">
                    <Field label="Groom's name" required testId="field-groom-name">
                        <TextInput
                            placeholder="e.g. Ishaan"
                            value={groomName}
                            onChange={(e) => setGroomName(e.target.value)}
                            data-testid="input-groom-name"
                        />
                    </Field>
                    <PhotoGuidelines />
                    <PhotoUpload
                        label="Primary groom photo"
                        subLabel="Used for skin-tone analysis + as his main reference. Must be a different photo from the bride's."
                        onValid={setGroom}
                        onClear={() => { setGroom(null); setGroomExtras([]); }}
                        compareAgainstPhash={bride?.phash || null}
                        testIdPrefix="groom"
                    />
                    {groom && (
                        <ExtraPhotos
                            person="groom"
                            primaryPhash={groom.phash}
                            extras={groomExtras}
                            setExtras={setGroomExtras}
                        />
                    )}
                </div>
            )}

            {step === 3 && (
                <div className="space-y-6" data-testid="step-events">
                    <p className="text-sm text-ink">
                        Add every ceremony you want covered. The main event is required — add up to 4 more.
                        Each event has its own venue and style so every visual matches perfectly.
                    </p>
                    {preselectedPack && eventsBrief.length < preselectedPack && (
                        <p className="text-sm text-gold" data-testid="pack-events-hint">
                            Tip: you booked the {preselectedPack}-event pack — brief {preselectedPack - eventsBrief.length} more event{preselectedPack - eventsBrief.length === 1 ? "" : "s"} to make full use of it.
                        </p>
                    )}

                    {eventsBrief.map((ev, i) => (
                        <EventCard
                            key={i}
                            index={i}
                            ev={ev}
                            onChange={(patch) => setEventsBrief((prev) => prev.map(
                                (e, idx) => idx === i ? { ...e, ...patch } : e,
                            ))}
                            onRemove={i === 0 ? null : () => setEventsBrief(
                                (prev) => prev.filter((_, idx) => idx !== i),
                            )}
                        />
                    ))}

                    {eventsBrief.length < 5 && (
                        <button
                            type="button"
                            onClick={() => setEventsBrief((prev) => [...prev, { name: "", venue_type: "", style: "" }])}
                            data-testid="btn-add-event"
                            className="inline-flex items-center gap-2 rounded-full border border-dashed border-charcoal/25 bg-white px-5 py-3 text-[13px] text-charcoal transition hover:border-gold hover:bg-champagne/40"
                        >
                            <Plus className="h-4 w-4 text-gold" /> Add another event
                            <span className="ml-1 text-charcoal/50">({eventsBrief.length}/5)</span>
                        </button>
                    )}
                </div>
            )}

            {step === 4 && (
                <div className="space-y-8" data-testid="step-color-brief">
                    {/* Feb-2026 patch — Wedding Style is the primary content of
                       step 4. The colour brief below is still optional and
                       kept for backward-compat. */}
                    <WeddingStyleStep value={weddingStyle} onChange={setWeddingStyle} />

                    <div className="rounded-[20px] border border-dashed border-charcoal/15 p-5 md:p-6">
                        <div className="flex items-baseline justify-between gap-2">
                            <div>
                                <div className="text-[10.5px] uppercase tracking-[0.22em] text-charcoal/60">Colour brief</div>
                                <div className="mt-1 text-[13px] text-ink">Optional but powerful — skip any field.</div>
                            </div>
                        </div>
                        <div className="mt-4 space-y-4">
                            <Field label="Venue city (optional)" testId="field-venue-city">
                                <TextInput
                                    placeholder="e.g. Jaipur, Udaipur, Goa, Delhi"
                                    value={venueCity}
                                    onChange={(e) => setVenueCity(e.target.value)}
                                    data-testid="input-venue-city"
                                />
                            </Field>
                            <Field label="Favourite colours (2-4 recommended)" testId="field-fav-colors">
                                <ChipInput values={favColors} setValues={setFavColors} placeholder="Type a colour + Enter · e.g. Emerald" testId="chip-fav" />
                            </Field>
                            <Field label="Colours to avoid entirely" testId="field-avoid-colors">
                                <ChipInput values={avoidColors} setValues={setAvoidColors} placeholder="Type a colour + Enter · e.g. Neon Pink" testId="chip-avoid" />
                            </Field>
                        </div>
                    </div>
                </div>
            )}

            {step === 5 && (
                <div className="space-y-5" data-testid="step-contact">
                    <Field label="WhatsApp number" required testId="field-phone">
                        <div className="flex gap-2">
                            <TextInput
                                placeholder="10-digit number"
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                data-testid="input-phone"
                                disabled={otpVerified}
                            />
                            <button
                                type="button"
                                onClick={sendOtp}
                                disabled={loading || otpVerified}
                                data-testid="btn-send-otp"
                                className="shrink-0 rounded-full border border-charcoal/15 bg-white px-5 py-3.5 text-sm text-charcoal transition hover:border-gold disabled:opacity-60"
                            >
                                {otpSent ? "Resend" : "Send OTP"}
                            </button>
                        </div>
                    </Field>
                    {devOtp && !otpVerified && (
                        <div className="flex items-center gap-2 rounded-[14px] bg-amber-50 p-3 text-xs text-charcoal ring-1 ring-amber-100">
                            <MessageCircle className="h-4 w-4 text-amber-600" />
                            SMS delivery is <strong className="mx-1">mocked</strong>. Your OTP is <strong className="ml-1">{devOtp}</strong>.
                        </div>
                    )}
                    {otpSent && (
                        <Field label="Enter OTP" required testId="field-otp">
                            <div className="flex gap-2">
                                <TextInput
                                    placeholder="6-digit OTP"
                                    maxLength={6}
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                    data-testid="input-otp"
                                    disabled={otpVerified}
                                />
                                <button
                                    type="button"
                                    onClick={verifyOtp}
                                    disabled={loading || otpVerified || otp.length !== 6}
                                    data-testid="btn-verify-otp"
                                    className="shrink-0 rounded-full bg-charcoal px-5 py-3.5 text-sm text-ivory transition hover:bg-charcoal/90 disabled:opacity-60"
                                >
                                    {otpVerified ? (
                                        <>
                                            <CheckCircle2 className="mr-2 inline h-4 w-4" /> Verified
                                        </>
                                    ) : (
                                        "Verify"
                                    )}
                                </button>
                            </div>
                        </Field>
                    )}
                    <p className="text-xs text-ink">We&apos;ll send your full report to this number.</p>

                    <ConsentBlock
                        ownership={ownershipConsent}
                        setOwnership={setOwnershipConsent}
                        aiDisclaimer={aiDisclaimerConsent}
                        setAiDisclaimer={setAiDisclaimerConsent}
                    />
                </div>
            )}

            <div className="mt-10">
                <PrimaryButton onClick={goNext} disabled={!canNext || loading} testId="q-next">
                    {loading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Working…
                        </>
                    ) : step === 5 ? (
                        <>
                            Continue to my pack <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                    ) : (
                        <>
                            Continue <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                    )}
                </PrimaryButton>
                <p className="mt-4 text-center text-xs text-ink/70">
                    No payment required to upload · Pay only when you confirm your pack
                </p>
                {step === 5 && <LegalFooter testId="q-legal-footer" />}
            </div>
        </QuestionnaireShell>
    );
}
